/**
 * Sign in (S01, S02, S03) and the emailed link. No passwords exist. The link page never signs in on GET: the
 * button POSTs, so email scanners that pre-fetch links can't use them up (docs/53 §4.1). Nothing here asks the
 * person to remember anything (WCAG 3.3.8): the code can be pasted or autofilled.
 */
import { Button, EmptyState, OtpInput, TextField } from '@ff/app-ui';
import { Email } from '@ff/contracts';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { ApiError, call, errorText } from '../lib/api.ts';
import { location, navigate } from '../lib/router.ts';
import { boot, signOut, useSession } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { S } from '@ff/app-ui/copy';
import { Logo } from '../ui/shell.tsx';
import { createPasskey, passkeysSupported } from '../ui/webauthn.ts';
import { PAID_EMAIL_KEY, PAID_SLUG_KEY, accountReady } from '../lib/paid.ts';

function Shell({ title, children }: { title: string; children: preact.ComponentChildren }): JSX.Element {
  useEffect(() => {
    document.title = `${title} · ${S.app.name}`;
  }, [title]);
  return (
    <div class="auth">
      <header class="auth-top">
        <Logo />
        <span>{S.app.name}</span>
      </header>
      <main class="auth-main" id="main">
        <h1>{title}</h1>
        {children}
      </main>
    </div>
  );
}

function nextPath(): string {
  const n = location.get().query.get('next');
  return n && /^\/(?!\/)/.test(n) ? n : '/';
}

/** `paid` is the page straight after Checkout when we don't have their email to hand: they're in, and we ask for it. */
export function SignInScreen({ paid = false, notice = null }: { paid?: boolean; notice?: string | null } = {}): JSX.Element {
  const [email, setEmail] = useState(() => sessionStorage.getItem('ff-signin-email') ?? '');
  const [error, setError] = useState<string | null>(notice);
  const [busy, setBusy] = useState(false);
  const submit = async (e: Event) => {
    e.preventDefault();
    const parsed = Email.safeParse(email.trim());
    if (!parsed.success) {
      setError(S.signin.emailError);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await call('authEmailStart', { body: { email: parsed.data, next: nextPath() } });
      sessionStorage.setItem('ff-signin-email', parsed.data);
      sessionStorage.setItem('ff-signin', JSON.stringify({ masked: r.maskedEmail, resendAt: Date.now() + r.resendAfterSeconds * 1000 }));
      navigate(`/signin/check${window.location.search}`);
    } catch (err) {
      setError(errorText(err, S.errors.offlineSend));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title={paid ? S.signin.paidTitle : S.signin.title}>
      <p class="auth-lead">{paid ? S.signin.paidAskLead : S.signin.lead}</p>
      <form class="stack" onSubmit={submit} noValidate>
        <TextField label={S.signin.email} type="email" inputMode="email" autoComplete="username webauthn" autoCapitalize="none" spellcheck={false} enterKeyHint="send" value={email} onInput={setEmail} error={error} />
        <Button type="submit" variant="primary" size="lg" block busy={busy}>
          {S.signin.send}
        </Button>
      </form>
      {!paid && <p class="t-muted auth-foot">{S.signin.newHere}</p>}
    </Shell>
  );
}

function PasskeyOffer({ onDone }: { onDone: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <div class="stack">
      <p>{S.signin.passkeyLead}</p>
      {msg && <p role="status">{msg}</p>}
      <Button
        variant="primary"
        size="lg"
        block
        busy={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const opts = await call('authPasskeyRegisterOptions');
            const credential = await createPasskey(opts);
            await call('authPasskeyRegisterVerify', { body: { credential } });
            setMsg(S.signin.passkeyDone);
            setTimeout(onDone, 1200);
          } catch {
            setMsg(S.signin.passkeyFailed);
          } finally {
            setBusy(false);
          }
        }}
      >
        {S.signin.passkeyOn}
      </Button>
      <Button variant="quiet" block onClick={onDone}>
        {S.signin.passkeyLater}
      </Button>
    </div>
  );
}

function useResendTimer() {
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('ff-signin') ?? 'null') as { masked: string; resendAt: number } | null;
    } catch {
      return null;
    }
  })();
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil(((saved?.resendAt ?? 0) - Date.now()) / 1000)));
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return { masked: saved?.masked ?? null, left, restart: (s: number) => setLeft(s) };
}

export function CheckEmailScreen({ paid = false }: { paid?: boolean } = {}): JSX.Element {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const timer = useResendTimer();
  const loc = useStore(location);

  const finish = async (next: string) => {
    await boot();
    sessionStorage.removeItem('ff-signin');
    navigate(next, { replace: true });
  };

  const verify = async (c: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await call('authEmailVerify', { body: { code: c } });
      const next = loc.query.get('next') && /^\/(?!\/)/.test(loc.query.get('next')!) ? loc.query.get('next')! : r.next;
      if (r.offerPasskey && passkeysSupported()) setOffer(next);
      else await finish(next);
    } catch (e) {
      setError(errorText(e, S.errors.offlineSend));
      if (e instanceof ApiError && (e.code === 'code_invalid' || e.code === 'code_expired')) setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (offer)
    return (
      <Shell title={S.signin.passkeyTitle}>
        <PasskeyOffer onDone={() => void finish(offer)} />
      </Shell>
    );

  return (
    <Shell title={paid ? S.signin.paidTitle : S.signin.checkTitle}>
      {timer.masked && <p class="auth-lead">{paid ? S.signin.paidLead(timer.masked) : S.signin.checkLead(timer.masked)}</p>}
      <form
        class="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) void verify(code);
        }}
      >
        <OtpInput label={S.signin.codeLabel} digitLabel={S.signin.digit} value={code} onChange={setCode} onComplete={(c) => void verify(c)} error={error} disabled={busy} />
        <Button type="submit" variant="primary" size="lg" block busy={busy} disabled={code.length !== 6}>
          {S.signin.verify}
        </Button>
      </form>
      <p class="t-muted">{S.signin.useLink}</p>
      <div class="stack" data-gap="s">
        {timer.left > 0 ? (
          <p class="t-small t-muted" aria-live="polite">{S.signin.resendIn(timer.left)}</p>
        ) : (
          <Button
            variant="secondary"
            block
            onClick={async () => {
              const email = sessionStorage.getItem('ff-signin-email');
              if (!email) return navigate('/signin');
              try {
                const r = await call('authEmailStart', { body: { email } });
                timer.restart(r.resendAfterSeconds);
                setNote(S.signin.resent);
              } catch (e) {
                setError(errorText(e, S.errors.offlineSend));
              }
            }}
          >
            {S.signin.resend}
          </Button>
        )}
        {note && <p role="status" class="t-small">{note}</p>}
        <Button variant="quiet" block href={`/signin${window.location.search}`}>
          {S.signin.wrongEmail}
        </Button>
      </div>
    </Shell>
  );
}

/**
 * Where Checkout sends someone who has just paid (`/signin/paid`). They're in, so it says so and sends the sign in
 * link to the email they paid with: the prospect page left it in this tab's storage. The link has to be asked for from
 * this browser (it's bound to it), and the account is made by Stripe's webhook, so we wait a few seconds for that
 * before asking. With no email to hand (another browser, a cleared tab) it asks for it, still saying they're in.
 */
export function PaidScreen(): JSX.Element {
  const stored = (() => {
    try {
      return sessionStorage.getItem(PAID_EMAIL_KEY);
    } catch {
      return null;
    }
  })();
  const [phase, setPhase] = useState<'sending' | 'sent' | 'ask'>(stored ? 'sending' : 'ask');
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!stored) return;
    let cancelled = false;
    void (async () => {
      const slug = sessionStorage.getItem(PAID_SLUG_KEY);
      if (slug) await accountReady(slug);
      try {
        const r = await call('authEmailStart', { body: { email: stored, next: nextPath() } });
        sessionStorage.setItem('ff-signin-email', stored);
        sessionStorage.setItem('ff-signin', JSON.stringify({ masked: r.maskedEmail, resendAt: Date.now() + r.resendAfterSeconds * 1000 }));
        sessionStorage.removeItem(PAID_EMAIL_KEY);
        sessionStorage.removeItem(PAID_SLUG_KEY);
        if (!cancelled) setPhase('sent');
      } catch {
        if (cancelled) return;
        setNotice(S.signin.paidSendFailed);
        setPhase('ask');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  if (phase === 'sent') return <CheckEmailScreen paid />;
  if (phase === 'ask') return <SignInScreen paid notice={notice} />;
  return (
    <Shell title={S.signin.paidTitle}>
      <p class="auth-lead" role="status" aria-live="polite">{S.signin.paidSending}</p>
    </Shell>
  );
}

export function LinkScreen({ token }: { token: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Shell title={S.signin.linkTitle}>
      <p class="auth-lead">{S.signin.linkLead}</p>
      {error && <p class="field-error" role="alert">{error}</p>}
      <Button
        variant="primary"
        size="lg"
        block
        busy={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const r = await call('authLinkConfirm', { body: { token } });
            if (r.result === 'signed_in') {
              await boot();
              navigate(r.signedIn.next, { replace: true });
            } else {
              sessionStorage.setItem('ff-signin', JSON.stringify({ masked: r.maskedEmail, resendAt: Date.now() + 30_000 }));
              setError(S.signin.linkNeedCode(r.maskedEmail));
              setTimeout(() => navigate('/signin/check', { replace: true }), 2500);
            }
          } catch (e) {
            setError(errorText(e, S.errors.offlineSend));
          } finally {
            setBusy(false);
          }
        }}
      >
        {S.signin.linkButton}
      </Button>
    </Shell>
  );
}

export function NoAccessScreen(): JSX.Element {
  const s = useSession();
  return (
    <Shell title={S.signin.noAccessTitle}>
      <EmptyState title={s.status === 'no_access' ? s.me.user.email : S.signin.noAccessTitle} body={S.signin.noAccessBody} action={<Button onClick={() => void signOut()}>{S.account.rows.signOut}</Button>} />
    </Shell>
  );
}
